import {useLayoutEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import DateTimeV2, {DATETIME_VIEW} from "../../../../../components/datetime-v2/datetime-v2";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";
import FontBody2 from "../../../../../components/fonts/font-body2/font-body2";
import "./rma-report-expanded-box.scss";
import {IReport} from "../../../../../services/api/report-service";
import ReportStore from "../../../../../stores/report-store";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";

export interface ITooltipBoxProps {
    refElement: HTMLSpanElement | null,
    onClick: () => void,
    onGenerateNewClick: () => void,
    report?: IReport,
    isGeneratingReport: boolean,
    reportStore: ReportStore,
}

const RmaReportExpandedBox: React.FC<ITooltipBoxProps> = ({ refElement, onClick, onGenerateNewClick, report, isGeneratingReport, reportStore }) => {
    const parentElement = document.body;
    const expandedBoxRef = useRef<HTMLDivElement>(null);
    const dateTimeParams = useDateTimeFormattingParams();


    const [position, setPosition] = useState({ x: 0, y: 0 });

    /*
     * useLayoutEffect runs synchronously immediately after React has performed all DOM mutations.
     * This can be useful if you need to make DOM measurements (like getting the scroll position or
     * other styles for an element) and then make DOM mutations or trigger a synchronous re-render
     * by updating state.
     * 
     * In this case we measure width of button and dropdown and update x position accordingly
     * so the useLayoutEffect is more appropriate here rather than useEffect
     */
    useLayoutEffect(() => {
        const getPosition = () => {
            const rect = refElement?.getBoundingClientRect();
            const expandedBoxRect = expandedBoxRef.current?.getBoundingClientRect();
            const expandedBoxWidth = expandedBoxRect ? expandedBoxRect.width : 0;
            if (rect) {
                const x = rect.x - (expandedBoxWidth - rect.width) + window.scrollX;
                const y = rect.y + rect.height + window.scrollY;
                setPosition({ x: x, y: y });
            }
        }

        if (refElement && expandedBoxRef.current) {
            getPosition();
        }


    }, [refElement, expandedBoxRef]);

    const {getTranslation} = useDashboardTranslation();
    const scope = 'dashboard-returns-page'; 

    const onDownloadClick = () => {
        if (!report) {
            return;
        }
        window.open(report.file_path, '_blank');
    }

    return createPortal(
        (
            <>
                <div
                    className={`rma-reportexpanded-box-overlay`}
                    onClick={(e) => { e.stopPropagation(); onClick() }}
                ></div>
                <div
                    className={`rma-reportexpanded-box`}
                    style={{ left: position.x, top: position.y }}
                    onClick={(e) => e.stopPropagation()}
                    ref={expandedBoxRef}
                >
                    {!isGeneratingReport && (
                        <div onClick={onGenerateNewClick}>
                            <FontBody2>{getTranslation('GENERATE_NEW_REPORT', scope)}</FontBody2>
                        </div>
                    )}
                    <div onClick={onDownloadClick}>
                        <FontBody2>{getTranslation('DOWNLOAD_LAST_REPORT', scope)}</FontBody2>
                        <FontBody2>
                            <DateTimeV2 {...dateTimeParams} utcDateString={reportStore.getLastGeneratedUTCString()} dateTimeView={DATETIME_VIEW.FULL} />
                        </FontBody2>
                    </div>
                </div>
            </>
        ),
        parentElement
    );
}

export default RmaReportExpandedBox;
